import { z } from 'zod';

import type { Env, EvalResult } from '../env.ts';
import { bind, checkBindable } from '../env.ts';
import { RebindingError, ShapeError } from '../errors.ts';
import { type AstInput, astShape, nodePos } from '../normalize.ts';
import { opMeta, type SpecialCtx } from '../op-meta.ts';
import { elemPath, paramPath } from '../path.ts';
import { astArg } from '../schema/ast-codec.ts';
import { LambdaValue } from '../value.ts';
import { SymbolRef } from './helpers.ts';

// Top-level forms.

/**
 * Top-level binding form. `(def name expr)` evaluates `expr` and binds
 * `name` to its value in the current scope. Subsequent top-level forms
 * see the binding; the binding is visible across the script and (via
 * the recipe-projection layer) across conversation turns.
 *
 * `def`'s RETURN value is `expr`'s value (matches the
 * "all expressions return a Value" rule).
 *
 * Top-level only: `def` inside any nested position
 * (`(let [a (def b 1)] …)` etc.) is a `ShapeError`. Enforced by the
 * path walker `findMisplacedDefs` in `evaluator.ts::evalScript`.
 */
export const def = z
  .object({
    name: z.literal('def'),
    params: z.tuple([SymbolRef, astArg()]),
  })
  .describe(
    'Top-level binding. `(def name expr)` evaluates `expr` and binds `name` to its value. Accepts any Value (Scalar, Dataset, Lambda, …). Top-level only.',
  )
  .register(opMeta, {
    docHidden: true,
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(def wells (query "WellAsset" (filter #(= (:type %) "production")) (limit 100)))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [nameRef, body] = data.params;
      const namePos = nodePos(ctx.spans, paramPath(ctx.path, 0));
      // Validate the binding name before any RHS evaluation so
      // op-name shadowing / rebinding fails fast.
      checkBindable(ctx.env, nameRef.$sym, namePos);
      const bodyPath = paramPath(ctx.path, 1);
      const { value } = await ctx.evalNode(body, ctx.env, bodyPath);
      const env = bind(ctx.env, nameRef.$sym, value, namePos);
      return { value, env };
    },
  });

/**
 * Lexical scope. `(let [name1 expr1 name2 expr2 …] body)` introduces a
 * frame of bindings visible in `body` (and to subsequent sibling
 * bindings — bindings are *sequential*, Fennel/Clojure-style: `expr2`
 * may reference `name1`). Returns `body`'s value.
 *
 * The binding vector is a flat alternating sequence — pairs of
 * `(symbol, expression)`. An odd length, a non-symbol name slot, or a
 * name that shadows a registered op / a name already bound in scope
 * is rejected with the corresponding `ExprError`.
 *
 * Each RHS is evaluated in the extended env and bound to its value.
 * `body` evaluates in the final extended env; the returned env is the
 * caller's (let is an expression, not a binding form for the
 * surrounding script).
 */
export const letOp = z
  .object({
    name: z.literal('let'),
    params: z.tuple([z.array(astArg()), astArg()]),
  })
  .describe(
    'Lexical scope with sequential bindings. `(let [name1 expr1 name2 expr2 …] body)` binds the names in the body. Returns the body value.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: {
      expr: '(let [t 100 hot (filter wells #(> (:production %) t))] (limit hot 5))',
    },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [bindingsVec, body] = data.params;
      const bindingsPath = paramPath(ctx.path, 0);
      const bindingsVecLen = bindingsVec.length;
      if (bindingsVecLen % 2 !== 0) {
        throw new ShapeError(
          `(let …) bindings vector must have an even number of elements (name expr pairs); got ${bindingsVecLen}`,
          nodePos(ctx.spans, bindingsPath),
        );
      }
      // Pair the vector into (nameRef, expr, pairIndex) triples — flat
      // alternating `[name expr name expr …]` is the Fennel/Clojure
      // convention. Each pair's name slot must be a symbol; the expr
      // slot is any AstInput.
      const pairs = Array.from({ length: bindingsVecLen / 2 }, (_, i): readonly [AstInput, AstInput, number] => [
        bindingsVec[2 * i] as AstInput,
        bindingsVec[2 * i + 1] as AstInput,
        i,
      ]);
      // Validate name slots up front so a malformed binding form fails
      // fast (before any RHS work runs).
      const namedPairs = pairs.map(([nameNode, expr, i]): readonly [string, AstInput, number] => {
        const namePath = elemPath(bindingsPath, 2 * i);
        const nameShape = astShape(nameNode);
        if (nameShape.kind !== 'symbolRef') {
          throw new ShapeError(
            `(let …) binding name must be a symbol; got ${nameShape.kind}`,
            nodePos(ctx.spans, namePath),
          );
        }
        return [nameShape.sym, expr, i];
      });
      // Sequential bind: each step extends the prior env so a later
      // RHS may reference earlier names. Async reduce because each
      // RHS is evaluated via ctx.evalNode.
      const extendedEnv = await namedPairs.reduce<Promise<Env>>(async (envAccP, [name, expr, i]) => {
        const envAcc = await envAccP;
        const namePath = elemPath(bindingsPath, 2 * i);
        const exprPath = elemPath(bindingsPath, 2 * i + 1);
        const namePos = nodePos(ctx.spans, namePath);
        // checkBindable rejects op-name shadow + same-frame rebind via
        // RebindingError / ShadowingOpError (the same errors `def`
        // produces). This catches repeated names within one let.
        checkBindable(envAcc, name, namePos);
        const { value } = await ctx.evalNode(expr, envAcc, exprPath);
        return bind(envAcc, name, value, namePos);
      }, Promise.resolve(ctx.env));
      const bodyPath = paramPath(ctx.path, 1);
      const { value } = await ctx.evalNode(body, extendedEnv, bodyPath);
      // `let` is an expression — return the caller's env, not the
      // extended frame, so bindings don't leak past the body.
      return { value, env: ctx.env };
    },
  });

/**
 * Anonymous function constructor. `(fn [p1 p2 …] body)` builds a
 * `LambdaValue` capturing the lexical env at definition site
 * (closure). Invocation via `(myFn arg1 arg2 …)` is intercepted in
 * `evaluator.ts::evalOpCall` when the call's head resolves in env to a
 * function; the per-row hot path inlines the body via
 * `runtime/arquero.ts::compileExpr`.
 *
 * Params are bare symbols; duplicates raise `RebindingError` at the
 * duplicate's position. Arity-mismatched calls raise
 * `LambdaCallArityError` at the call site.
 */
export const fnOp = z
  .object({
    name: z.literal('fn'),
    params: z.tuple([z.array(SymbolRef), astArg()]),
  })
  .describe(
    'Anonymous function with closure-env capture. `(fn [p1 p2 …] body)` builds a callable Value; `(myFn a b)` invokes it. Free names in the body resolve against the env at definition site (closure semantics).',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(let [hot? (fn [r] (> r 100))] (hot? 150))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [paramSyms, body] = data.params;
      const paramsPath = paramPath(ctx.path, 0);
      const paramNames = paramSyms.map((p) => p.$sym);
      // Duplicate-param check with positioned error — surface the
      // OFFENDING (second-occurrence) param's position, not the
      // lambda head's. `findIndex` + `indexOf` returns the first
      // duplicate's index in O(n²) over a small array (lambda param
      // counts are tiny — this is faster in practice than building
      // a Set with index bookkeeping). The LambdaValue constructor's
      // TypeError is defence-in-depth for ill-formed direct
      // constructions.
      const dupIdx = paramNames.findIndex((n, i) => paramNames.indexOf(n) !== i);
      if (dupIdx !== -1) {
        throw new RebindingError(paramNames[dupIdx] as string, nodePos(ctx.spans, elemPath(paramsPath, dupIdx)));
      }
      return { value: new LambdaValue(paramNames, body, ctx.env), env: ctx.env };
    },
  });
